// src/App.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Form,
  FloatingLabel,
  Button,
  Card,
  Spinner,
  Alert,
  Modal,
  ListGroup
} from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';

export default function App() {
  const [categorias, setCategorias] = useState([]);
  const [categoriaSelecionada, setCategoriaSelecionada] = useState('');
  const [pesquisa, setPesquisa] = useState('');
  const [drinks, setDrinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [detalheBebida, setDetalheBebida] = useState(null);
  const [mostrarModal, setMostrarModal] = useState(false);

  // Carrega categorias de cocktails
  useEffect(() => {
    fetch('https://www.thecocktaildb.com/api/json/v1/1/list.php?c=list')
      .then(res => res.json())
      .then(data => setCategorias(data.drinks || []))
      .catch(() => setErro('Não foi possível obter categorias.'));
  }, []);

  // Busca cocktails genérico
  const buscarDrinks = url => {
    setLoading(true);
    setErro('');
    fetch(url)
      .then(res => res.json())
      .then(data => setDrinks(Array.isArray(data.drinks) ? data.drinks : []))
      .catch(() => setErro('Erro ao carregar cocktails.'))
      .finally(() => setLoading(false));
  };

  // Filtra por categoria
  useEffect(() => {
    if (!categoriaSelecionada) return;
    buscarDrinks(
      `https://www.thecocktaildb.com/api/json/v1/1/filter.php?c=${encodeURIComponent(categoriaSelecionada)}`
    );
  }, [categoriaSelecionada]);

  // Pesquisa por nome
  const aoSubmeterPesquisa = e => {
    e.preventDefault();
    if (!pesquisa.trim()) return;
    buscarDrinks(
      `https://www.thecocktaildb.com/api/json/v1/1/search.php?s=${encodeURIComponent(pesquisa.trim())}`
    );
  };

  // Função de tradução usando MyMemory (CORS suportado)
  const traduzirTexto = async texto => {
    try {
      const res = await fetch(
        `https://api.mymemory.translated.net/get?q=${encodeURIComponent(texto)}&langpair=en|pt`);
      const json = await res.json();
      return json.responseData.translatedText;
    } catch {
      return texto;
    }
  };

  // Abre modal e obtém detalhes, traduzindo instruções se necessário
  const abrirDetalhes = async id => {
    setMostrarModal(true);
    setDetalheBebida(null);
    setErro('');
    try {
      const res = await fetch(`https://www.thecocktaildb.com/api/json/v1/1/lookup.php?i=${id}`);
      const data = await res.json();
      const b = data.drinks?.[0];
      if (!b) throw new Error();
      // Instruções em PT da API ou via tradução
      let instrucoes = b.strInstructionsPT?.trim();
      if (!instrucoes) {
        instrucoes = await traduzirTexto(b.strInstructions);
      }
      // Extrai ingredientes sem tradução para simplicidade
      const ingredientes = [];
      for (let i = 1; i <= 15; i++) {
        const nome = b[`strIngredient${i}`];
        const medida = b[`strMeasure${i}`] || '';
        if (nome) ingredientes.push({ nome, medida });
      }
      setDetalheBebida({
        titulo: b.strDrink,
        thumb: b.strDrinkThumb,
        instrucoes,
        ingredientes
      });
    } catch {
      setErro('Erro ao obter detalhes da receita.');
    }
  };

  return (
    <Container className="py-5">
      <h1 className="mb-4">GinLover.pt – Catálogo de Cocktails</h1>

      <Form onSubmit={aoSubmeterPesquisa} className="mb-4">
        <Row className="g-2">
          <Col md={5}>
            <FloatingLabel label="Pesquisar por nome">
              <Form.Control
                type="text"
                placeholder="Ex: Caipirinha"
                value={pesquisa}
                onChange={e => setPesquisa(e.target.value)}
              />
            </FloatingLabel>
          </Col>
          <Col md={4}>
            <FloatingLabel label="Filtrar por categoria">
              <Form.Select
                value={categoriaSelecionada}
                onChange={e => { setCategoriaSelecionada(e.target.value); setPesquisa(''); }}
              >
                <option value="">— todas —</option>
                {categorias.map((c, i) => (
                  <option key={i} value={c.strCategory}>{c.strCategory}</option>
                ))}
              </Form.Select>
            </FloatingLabel>
          </Col>
          <Col md={3}>
            <Button type="submit" variant="primary" className="w-100">Pesquisar</Button>
          </Col>
        </Row>
      </Form>

      {erro && <Alert variant="danger">{erro}</Alert>}
      {loading && <div className="text-center mb-4"><Spinner animation="border"/> Carregando...</div>}

      <Row xs={1} md={3} lg={4} className="g-4">
        {drinks.map(d => (
          <Col key={d.idDrink}>
            <Card className="h-100">
              <Card.Img variant="top" src={d.strDrinkThumb} />
              <Card.Body><Card.Title>{d.strDrink}</Card.Title></Card.Body>
              <Card.Footer>
                <Button size="sm" variant="outline-secondary" onClick={() => abrirDetalhes(d.idDrink)}>
                  Ver Receita
                </Button>
              </Card.Footer>
            </Card>
          </Col>
        ))}
      </Row>

      <Modal show={mostrarModal} onHide={() => setMostrarModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{detalheBebida?.titulo || '...Carregando'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!detalheBebida ? (
            <div className="text-center"><Spinner animation="border"/></div>
          ) : (
            <Row>
              <Col md={4}>
                <img src={detalheBebida.thumb} alt={detalheBebida.titulo} className="img-fluid rounded" />
              </Col>
              <Col md={8}>
                <h5>Ingredientes</h5>
                <ListGroup className="mb-3">
                  {detalheBebida.ingredientes.map((ing, i) => (
                    <ListGroup.Item key={i}>{ing.nome} {ing.medida}</ListGroup.Item>
                  ))}
                </ListGroup>
                <h5>Instruções</h5>
                <p>{detalheBebida.instrucoes}</p>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setMostrarModal(false)}>Fechar</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
